import { useMemo, useRef, useState } from "react";

import { ACCESS, billing as billingOf, trial as trialOf, formatNumber } from "./licensingTheme";
import { useDrawLines, useBarsRise } from "./licensingMotion";
import { ChartTooltip, useChartTooltip } from "./ChartTooltip";

const TRIAL_ORDER = ["trial_1d", "trial_3d", "trial_active", "trial_converted", "trial_expired", "never_trialled"];
const BILLING_ORDER = [
  "paid_yearly",
  "paid_monthly",
  "cancelling",
  "grace",
  "app_store",
  "paid_no_record",
  "lapsed",
  "refunded",
  "none",
  "other",
];

const ROW = 56;
const GAP = 10;
const MID_W = 400;
const MAX_STROKE = 26;

const pct = (value, total) => (total ? `${Math.round((value / total) * 1000) / 10}%` : "—");
const centerY = (index) => index * (ROW + GAP) + ROW / 2;

/**
 * Where every teacher stands: the trial on the left, their own subscription on
 * the right, and a ribbon for every pair that actually has teachers in it.
 *
 * Rows are all the same height on purpose. Sized by count, a state holding
 * three teachers became a three-pixel sliver whose label ran into its
 * neighbour's — the smallest groups are the ones worth reading (a failed
 * payment, a refund), and they were the ones the chart made unreadable. The
 * count now lives in the number and the thin bar under it; the ribbon's
 * thickness carries the flow.
 *
 * Ribbons are measured, never implied: each is the server's count of teachers
 * in that trial state AND that billing state.
 */
export default function LifecycleFlow({ overview, loading, onDrill }) {
  const scope = useRef(null);
  const { tip, bind, hide } = useChartTooltip();
  const [focus, setFocus] = useState(null);

  const trials = overview?.trials ?? {};
  const billingCounts = overview?.billing ?? {};
  const access = overview?.access ?? {};
  const flow = overview?.flow ?? [];
  const population = overview?.population ?? 0;
  const shapeKey = JSON.stringify([trials, billingCounts, flow, population]);

  const { left, right, ribbons, height } = useMemo(() => {
    const leftRows = TRIAL_ORDER.filter((key) => (trials[key] ?? 0) > 0).map((key) => ({
      key,
      side: "left",
      label: trialOf(key).label,
      color: trialOf(key).color,
      value: trials[key] ?? 0,
      filters: { trialState: key },
    }));
    const rightRows = BILLING_ORDER.filter((key) => (billingCounts[key] ?? 0) > 0).map((key) => ({
      key,
      side: "right",
      label: billingOf(key).label,
      color: billingOf(key).color,
      value: billingCounts[key] ?? 0,
      filters: { billingState: key },
    }));

    const leftIndex = new Map(leftRows.map((row, i) => [row.key, i]));
    const rightIndex = new Map(rightRows.map((row, i) => [row.key, i]));
    const links = flow.filter((link) => link.count > 0 && leftIndex.has(link.from) && rightIndex.has(link.to));
    const max = Math.max(1, ...links.map((l) => l.count));

    // Several ribbons leave one row; fan them across the row instead of
    // stacking them on one point, ordered so they do not cross on the way out.
    const spread = (list, keyOf, order) => {
      const groups = new Map();
      list.forEach((link) => {
        const k = keyOf(link);
        if (!groups.has(k)) groups.set(k, []);
        groups.get(k).push(link);
      });
      const offsets = new Map();
      groups.forEach((group) => {
        group.sort(order);
        const step = Math.min(10, (ROW * 0.6) / Math.max(group.length, 1));
        group.forEach((link, i) => offsets.set(link, (i - (group.length - 1) / 2) * step));
      });
      return offsets;
    };
    const outOffsets = spread(links, (l) => l.from, (a, b) => rightIndex.get(a.to) - rightIndex.get(b.to));
    const inOffsets = spread(links, (l) => l.to, (a, b) => leftIndex.get(a.from) - leftIndex.get(b.from));

    const drawn = links.map((link) => {
      const y0 = centerY(leftIndex.get(link.from)) + outOffsets.get(link);
      const y1 = centerY(rightIndex.get(link.to)) + inOffsets.get(link);
      // Square root, so a group of 300 does not drown a group of 3: area, not
      // width, is what the eye compares.
      const width = Math.max(2, Math.sqrt(link.count / max) * MAX_STROKE);
      return {
        ...link,
        width,
        // Coloured by where the ribbon ends: the eye follows a ribbon to find
        // out what became of that group, so the destination is what it should
        // already be showing. "Not subscribed" is darkened from its node colour,
        // which is too pale to read as a line.
        color: link.to === "none" ? "#9fb0a8" : billingOf(link.to).color,
        d: `M 0 ${y0} C ${MID_W * 0.5} ${y0}, ${MID_W * 0.5} ${y1}, ${MID_W} ${y1}`,
      };
    });

    const rows = Math.max(leftRows.length, rightRows.length, 1);
    return { left: leftRows, right: rightRows, ribbons: drawn, height: rows * (ROW + GAP) - GAP };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shapeKey]);

  useDrawLines(scope, "[data-ribbon]", [shapeKey]);
  useBarsRise(scope, [shapeKey], { selector: "[data-share]", axis: "x" });

  const isLit = (ribbon) =>
    !focus || (focus.side === "left" ? ribbon.from === focus.key : focus.side === "right" ? ribbon.to === focus.key : ribbon === focus);

  const ribbonTip = (ribbon) => ({
    title: `${trialOf(ribbon.from).label} → ${billingOf(ribbon.to).label}`,
    rows: [
      { label: "Teachers", value: formatNumber(ribbon.count), color: ribbon.color },
      { label: "% of all teachers", value: pct(ribbon.count, population) },
    ],
    footer: "Click to see who",
  });

  const rowTip = (row) => ({
    title: row.label,
    subtitle: row.side === "left" ? "Free trial" : "Plan today",
    rows: [
      { label: "Teachers", value: formatNumber(row.value), color: row.color },
      { label: "% of all teachers", value: pct(row.value, population) },
      ...ribbons
        .filter((r) => (row.side === "left" ? r.from === row.key : r.to === row.key))
        .sort((a, b) => b.count - a.count)
        .slice(0, 4)
        .map((r) => ({
          label: row.side === "left" ? `→ ${billingOf(r.to).label}` : `← ${trialOf(r.from).label}`,
          value: formatNumber(r.count),
          muted: true,
        })),
    ],
    footer: "Click to see who",
  });

  // A render function, not a component: defined inside render, a component
  // would remount on every hover and lose the pointer it is under.
  const renderRow = (row) => (
    <button
      key={row.key}
      type="button"
      className={`mcap-lic-journey-row ${focus && focus.key !== row.key && focus.side === row.side ? "is-dim" : ""}`}
      style={{ "--row-color": row.color }}
      onClick={() => {
        hide();
        onDrill({ type: "cohort", title: row.label, filters: row.filters });
      }}
      onMouseEnter={(event) => {
        setFocus(row);
        bind(rowTip(row)).onMouseEnter(event);
      }}
      onMouseMove={bind(rowTip(row)).onMouseMove}
      onMouseLeave={() => {
        setFocus(null);
        hide();
      }}
      onFocus={(event) => {
        setFocus(row);
        bind(rowTip(row)).onFocus(event);
      }}
      onBlur={() => {
        setFocus(null);
        hide();
      }}
      aria-label={`${row.label}: ${formatNumber(row.value)} teachers, ${pct(row.value, population)}. Open the list.`}
    >
      <span className="mcap-lic-journey-label">{row.label}</span>
      <span className="mcap-lic-journey-value">
        {formatNumber(row.value)}
        <small>{pct(row.value, population)}</small>
      </span>
      <span className="mcap-lic-journey-share" aria-hidden="true">
        <span data-share style={{ width: `${Math.max(1.5, (row.value / (population || 1)) * 100)}%` }} />
      </span>
    </button>
  );

  // The second axis, complete: why each teacher has premium right now, in the
  // order the app checks. Every teacher is in exactly one, so these add up to
  // the same total as both columns above.
  const sideNodes = [
    { key: "grandfathered", detail: "Joined before paid plans. Free for good." },
    { key: "individual", detail: "Pays for their own plan." },
    { key: "school_seat", detail: "Covered by their school." },
    { key: "trial", detail: "In the 14-day free trial." },
    { key: "none", detail: "Free version only." },
  ].map((node) => ({
    ...node,
    label: ACCESS[node.key].label,
    color: ACCESS[node.key].color,
    value: access[node.key] ?? 0,
    filters: { accessSource: node.key },
  }));

  return (
    <section className="mcap-lic-panel mcap-lic-flow-panel" aria-labelledby="mcap-lic-flow-title" ref={scope}>
      <header className="mcap-lic-panel-head">
        <div>
          <h3 id="mcap-lic-flow-title" className="mcap-lic-panel-title">
            Where everyone stands
          </h3>
          <p className="mcap-lic-panel-sub">
            {loading
              ? " "
              : `${formatNumber(population)} teachers. Left: free trial. Right: plan today. Hover a line for numbers.`}
          </p>
        </div>
      </header>

      {!loading && population === 0 && <p className="mcap-lic-empty">No teachers match these filters yet.</p>}

      {!loading && population > 0 && (
        <div className="mcap-lic-journey">
          <div className="mcap-lic-journey-col">
            <p className="mcap-lic-journey-heading">Free trial</p>
            <div className="mcap-lic-journey-rows" style={{ gap: GAP }}>
              {left.map(renderRow)}
            </div>
          </div>

          <div className="mcap-lic-journey-mid">
            <p className="mcap-lic-journey-heading">&nbsp;</p>
            <svg
              viewBox={`0 0 ${MID_W} ${height}`}
              preserveAspectRatio="none"
              style={{ height }}
              className="mcap-lic-journey-svg"
              aria-hidden="true"
            >
              {ribbons.map((ribbon) => (
                <path
                  key={`${ribbon.from}-${ribbon.to}`}
                  d={ribbon.d}
                  stroke={ribbon.color}
                  strokeWidth={ribbon.width}
                  fill="none"
                  vectorEffect="non-scaling-stroke"
                  strokeLinecap="round"
                  className={`mcap-lic-ribbon ${isLit(ribbon) ? (focus ? "is-lit" : "") : "is-dim"}`}
                  data-ribbon
                  onMouseEnter={(event) => {
                    setFocus(ribbon);
                    bind(ribbonTip(ribbon)).onMouseEnter(event);
                  }}
                  onMouseMove={(event) => bind(ribbonTip(ribbon)).onMouseMove(event)}
                  onMouseLeave={() => {
                    setFocus(null);
                    hide();
                  }}
                  onClick={() => {
                    hide();
                    onDrill({
                      type: "cohort",
                      title: `${trialOf(ribbon.from).label} → ${billingOf(ribbon.to).label}`,
                      filters: { trialState: ribbon.from, billingState: ribbon.to },
                    });
                  }}
                />
              ))}
            </svg>
          </div>

          <div className="mcap-lic-journey-col">
            <p className="mcap-lic-journey-heading">Plan today</p>
            <div className="mcap-lic-journey-rows" style={{ gap: GAP }}>
              {right.map(renderRow)}
            </div>
          </div>
        </div>
      )}

      <div className="mcap-lic-access">
        <p className="mcap-lic-journey-heading">How they get premium</p>
        <div className="mcap-lic-side-nodes">
          {sideNodes.map((node) => (
            <button
              key={node.key}
              type="button"
              className="mcap-lic-side-node"
              disabled={!node.value}
              style={{ "--row-color": node.color }}
              {...bind({
                title: node.label,
                subtitle: node.detail,
                rows: [
                  { label: "Teachers", value: formatNumber(node.value), color: node.color },
                  { label: "% of all teachers", value: pct(node.value, population) },
                ],
                footer: node.value ? "Click to see who" : undefined,
              })}
              onClick={() => {
                hide();
                onDrill({ type: "cohort", title: node.label, filters: node.filters });
              }}
            >
              <span className="mcap-lic-side-label">{node.label}</span>
              <span className="mcap-lic-side-value">{node.value ? formatNumber(node.value) : "None"}</span>
            </button>
          ))}
        </div>
      </div>

      <ChartTooltip tip={tip} />
    </section>
  );
}
